import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";

interface ServicePrice {
  id: string;
  service_type: string;
  base_price: number;
  effective_from: string;
  effective_to: string | null;
}

export function ServicePricingTable() {
  const [prices, setPrices] = useState<ServicePrice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ServicePrice>>({});
  const { toast } = useToast();

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      const { data, error } = await supabase
        .from('service_pricing')
        .select('*')
        .order('effective_from', { ascending: false });

      if (error) throw error;
      setPrices(data || []);
    } catch (error) {
      console.error('Error fetching prices:', error);
      toast({
        title: "Error",
        description: "Failed to load service prices",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (price: ServicePrice) => {
    setEditingId(price.id);
    setEditForm(price);
  };

  const handleSave = async () => {
    try {
      if (!editingId || !editForm.base_price) return;

      const { error } = await supabase
        .from('service_pricing')
        .update({
          base_price: editForm.base_price,
          effective_from: editForm.effective_from,
          effective_to: editForm.effective_to,
        })
        .eq('id', editingId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Price updated successfully",
      });

      setEditingId(null);
      setEditForm({});
      fetchPrices();
    } catch (error) {
      console.error('Error updating price:', error);
      toast({
        title: "Error",
        description: "Failed to update price",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  if (isLoading) {
    return <div className="flex justify-center p-4">Loading...</div>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Service Type</TableHead>
            <TableHead>Base Price</TableHead>
            <TableHead>Effective From</TableHead>
            <TableHead>Effective To</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prices.map((price) => (
            <TableRow key={price.id}>
              <TableCell>{price.service_type}</TableCell>
              <TableCell>
                {editingId === price.id ? (
                  <Input
                    type="number"
                    value={editForm.base_price}
                    onChange={(e) =>
                      setEditForm({ ...editForm, base_price: parseFloat(e.target.value) })
                    }
                    className="w-24"
                  />
                ) : (
                  `$${price.base_price.toFixed(2)}`
                )}
              </TableCell>
              <TableCell>
                {format(new Date(price.effective_from), 'dd/MM/yyyy')}
              </TableCell>
              <TableCell>
                {price.effective_to
                  ? format(new Date(price.effective_to), 'dd/MM/yyyy')
                  : 'Current'}
              </TableCell>
              <TableCell>
                {editingId === price.id ? (
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={handleSave}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => handleEdit(price)}>
                    Edit
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}